import { z } from 'zod';
import {
  paginatedResponse,
  paginationQuery,
  nonNegativeDecimalString as decimalString,
} from './common';

/**
 * Schemas for the dedicated `Material` catalog table — one of the five pricing-
 * engine entity tables (Material, Edge, DrawerSystem, Hardware, Lighting).
 *
 * `pricePerM2` is the canonical price (RON-by-default) used by the Cabinet,
 * Panel, Countertop, etc. calculators. Hidden from callers without
 * `CATALOG_READ_COST` (the API omits it at DTO time, so it is wire-optional).
 *
 * `glassSanding` is the legacy "Slefuire Sticla" markup flag — when true, the
 * pricing engine multiplies the front area by `glassSandingMarkup` (1.30 by
 * default; admin-tunable in `SystemSetting`).
 *
 * Source data: `excels/materiale.xlsx` (sheets PAL/MDF/Sticla/Blat/Backsplash/PFL).
 */

export const materialCategoryEnum = z.enum([
  'PAL_CAT_1',
  'PAL_CAT_2',
  'PAL_CAT_3',
  'MDF_VOPSIT',
  'STICLA',
  'BLAT_TERMOREZISTENT',
  'BACKSPLASH',
  'PFL',
]);
export type MaterialCategoryValue = z.infer<typeof materialCategoryEnum>;

export const materialCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type MaterialCurrencyValue = z.infer<typeof materialCurrencyEnum>;

export const materialUsageScopeEnum = z.enum(['GENERAL', 'COUNTERTOP']);
export type MaterialUsageScopeValue = z.infer<typeof materialUsageScopeEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const optionalThicknessMm = z.number().int().nonnegative().max(500).optional().nullable();

export const materialListItem = z.object({
  id: z.string(),
  name: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  internalCode: z.string().nullable(),
  manufacturer: z.string().nullable(),
  category: materialCategoryEnum,
  usageScopes: z.array(materialUsageScopeEnum),
  structure: z.string().nullable(),
  thicknessMm: z.number().int().nullable(),
  pricePerM2: decimalString.optional(),
  currency: materialCurrencyEnum,
  /**
   * Default price multiplier for this board. Copied onto every item that uses
   * it and editable there, so changing it here never re-prices work already
   * quoted. Boards bought at a fixed price — painted, veneered — carry 1.
   */
  coefficient: decimalString,
  glassSanding: z.boolean(),
  url: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type MaterialListItem = z.infer<typeof materialListItem>;

export const materialDetails = materialListItem.extend({
  attributes: z.unknown().nullable(),
});
export type MaterialDetails = z.infer<typeof materialDetails>;

export const listMaterialsQuery = paginationQuery.extend({
  search: optionalTrim(120),
  category: materialCategoryEnum.optional(),
  usageScope: materialUsageScopeEnum.optional(),
  manufacturer: optionalTrim(120),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
});
export type ListMaterialsQuery = z.infer<typeof listMaterialsQuery>;

export const listMaterialsResponse = paginatedResponse(materialListItem);
export type ListMaterialsResponse = z.infer<typeof listMaterialsResponse>;

export const createMaterialRequest = z.object({
  name: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(80),
  internalCode: optionalTrim(120),
  manufacturer: optionalTrim(120),
  category: materialCategoryEnum,
  usageScopes: z.array(materialUsageScopeEnum).min(1).optional(),
  structure: optionalTrim(120),
  thicknessMm: optionalThicknessMm,
  pricePerM2: decimalString,
  currency: materialCurrencyEnum.default('RON'),
  coefficient: decimalString.default('2.50'),
  glassSanding: z.boolean().default(false),
  url: optionalTrim(500),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateMaterialRequest = z.infer<typeof createMaterialRequest>;

export const updateMaterialRequest = createMaterialRequest.partial();
export type UpdateMaterialRequest = z.infer<typeof updateMaterialRequest>;
